// Identidade de UMA linha de evento do WL. Eventos que valem para
// pedido e assinatura existem em DUAS linhas no backend (uma por
// transactionType) com o mesmo `name` — o card precisa saber qual é a
// sua para escrever só nela e para o cache otimista não tocar a outra.
import type {
  NotificationOriginTransactionType,
  NotificationWhiteLabelEventType,
} from "@/types/notification-center"

/**
 * transactionType da linha: o campo `transactionType` do backend quando
 * vier; senão, `origin` com UM elemento. Formato antigo
 * (`[ORDER, SUBSCRIPTION]` nas duas linhas, sem `transactionType`) não
 * distingue → `undefined` (as escritas saem sem o parâmetro e o backend
 * altera todas as linhas).
 */
export const resolveEventTransactionType = (
  event: Pick<
    NotificationWhiteLabelEventType,
    "origin" | "transactionType"
  >
): NotificationOriginTransactionType | undefined =>
  event.transactionType ??
  (event.origin.length === 1 ? event.origin[0] : undefined)

/**
 * A linha do cache é alvo da escrita? Casa por name e, quando a escrita
 * tem transactionType, também pela linha — sem ele, todas as linhas do
 * evento (espelha o backend sem o parâmetro).
 */
export const matchesEventRow = (
  event: Pick<
    NotificationWhiteLabelEventType,
    "name" | "origin" | "transactionType"
  >,
  eventName: string,
  transactionType?: NotificationOriginTransactionType
): boolean =>
  event.name === eventName &&
  (transactionType === undefined ||
    resolveEventTransactionType(event) === transactionType)

/**
 * Chave React única por card: name + linha (ou origin no formato antigo)
 * e um sufixo de ocorrência para as linhas indistinguíveis do formato
 * antigo, que caem no mesmo grupo com name e origin iguais.
 */
export const withEventRowKeys = <
  TEvent extends Pick<
    NotificationWhiteLabelEventType,
    "name" | "origin" | "transactionType"
  >,
>(
  events: TEvent[]
): { key: string; event: TEvent }[] => {
  const seen = new Map<string, number>()
  return events.map((event) => {
    const baseKey = `${event.name}:${
      resolveEventTransactionType(event) ??
      event.origin.join("+")
    }`
    const occurrence = (seen.get(baseKey) ?? 0) + 1
    seen.set(baseKey, occurrence)
    return {
      key:
        occurrence === 1
          ? baseKey
          : `${baseKey}#${occurrence}`,
      event,
    }
  })
}
